import { pipe } from 'ramda';
import React, { FC, useMemo } from 'react';
import { OptionTypeBase } from 'react-select';
import styled from 'styled-components';
import { DueConfiguration } from 'api/timesheets/configuration/configuration';
import { TimeOfDay, times } from 'api/timesheets/configuration/time-of-day';
import { Select } from 'components/atoms/select';
import { DayOfWeek, days } from 'core/day-of-week';
import { isEmpty, orElse } from 'core/maybe';
import { maybeValueOf } from 'core/state';
import { useTranslation } from 'hooks/use-translation';
import { HelperText01 } from 'ozone/atoms';
import { Box } from '../components';
import { ControlPanel } from '../control-panel';
import { SetState, State } from '../state';
import { useDay } from './use-day-of-week';
import { useTime } from './use-time-of-day';

const Input = styled(Select)`
  width: 150px;
`;

const dayOfWeekOptions = days().map((x) => ({
  label: x,
  value: x,
}));

const timeOfDayOptions = times().map((x) => ({
  value: x,
  label: x,
}));

interface Props {
  due: State<DueConfiguration>;
  onDueChange: SetState<DueConfiguration>;
}

export const DueSettings: FC<Props> = ({ due, onDueChange }) => {
  const label = useTranslation('settings.timesheets.controls.due.label');
  const description = useTranslation(
    'settings.timesheets.controls.due.description',
  );
  const connector = useTranslation(
    'settings.timesheets.controls.due.connector',
  );
  const [day, setDay] = useDay(due, onDueChange);
  const [time, setTime] = useTime(due, onDueChange);
  const loading = useMemo(() => pipe(maybeValueOf, isEmpty)(due), [due]);
  const selectedDay = useMemo(
    () =>
      pipe(
        () => day,
        maybeValueOf,
        orElse<DayOfWeek | null>(null),
        (x) => dayOfWeekOptions.find((o) => o.value === x),
      )(),
    [day],
  );
  const selectedTime = useMemo(
    () =>
      pipe(
        () => time,
        maybeValueOf,
        orElse<TimeOfDay | null>(null),
        (x) => timeOfDayOptions.find((o) => o.value === x),
      )(),
    [time],
  );

  return (
    <ControlPanel label={label} descriptions={[description]}>
      <Box>
        <Input
          disabled={loading}
          options={dayOfWeekOptions}
          value={selectedDay}
          onChange={(selected: OptionTypeBase) => setDay(selected.value)}
        />
        <HelperText01>{connector}</HelperText01>
        <Input
          disabled={loading}
          options={timeOfDayOptions}
          value={selectedTime}
          onChange={(selected: OptionTypeBase) => setTime(selected.value)}
        />
      </Box>
    </ControlPanel>
  );
};
